import React, { useEffect, Suspense, lazy } from 'react';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import LogoCarousel from '../components/LogoCarousel';
import Stats from '../components/Stats';
import About from '../components/About';
import Experience from '../components/Experience';
import Leadership from '../components/Leadership';
import Education from '../components/Education';
import Skills from '../components/Skills';
import Contact from '../components/Contact';
import Footer from '../components/Footer';
import OrganizationsFounded from '../components/WebsiteEmbeds';
import FeaturedPress from '../components/FeaturedPress';
import PhotoGallery from '../components/PhotoGallery';
import Certifications from '../components/Certifications';
import Projects from '../components/Projects';
import MobileContactFAB from '../components/MobileContactFAB';
import AIAssistant from '../components/AIAssistant';
import { ExitIntentModal } from '../components/ExitIntentModal';
import { SectionTracker } from '../components/SectionTracker';
import { AnalyticsPanel } from '../components/AnalyticsPanel';
import { useLanguage } from '../context/LanguageContext';
import { useAnalytics } from '../context/AnalyticsContext';
// Lazy load heavy components
const PortfolioPerformance = lazy(() => import('../components/PortfolioPerformance'));
const MonteCarloSimulation = lazy(() => import('../components/MonteCarloSimulation'));
const TravelMap = lazy(() => import('../components/TravelMap'));
// Loading fallback component
const LoadingFallback = () => <div className="w-full py-24 flex items-center justify-center" data-id="element-1438">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" data-id="element-1439"></div>
  </div>;
const Home = () => {
  const {
    language,
    toggleLanguage
  } = useLanguage();
  const {
    trackPageVisit
  } = useAnalytics();
  useEffect(() => {
    trackPageVisit('Home');
  }, [trackPageVisit]);
  useEffect(() => {
    const observerCallback = entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
          const elements = entry.target.querySelectorAll('.fade-in, .slide-in-left, .slide-in-right, .scale-in, .staggered-text, .slide-up');
          elements.forEach((el, index) => {
            setTimeout(() => {
              el.classList.add('active');
            }, index * 100);
          });
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    });
    document.querySelectorAll('section, .section-transition, .staggered-text, .slide-up').forEach(section => {
      observer.observe(section);
    });
    return () => {
      observer.disconnect();
    };
  }, []);
  return <div className="w-full min-h-screen bg-background text-primary font-body overflow-x-hidden" data-id="element-1440">
      <Navbar language={language} toggleLanguage={toggleLanguage} data-id="element-1441" />

      <SectionTracker sectionName="Hero" data-id="element-1442">
        <Hero language={language} toggleLanguage={toggleLanguage} data-id="element-1443" />
      </SectionTracker>

      <SectionTracker sectionName="Logo Carousel" data-id="element-1444">
        <LogoCarousel data-id="element-1445" />
      </SectionTracker>

      <SectionTracker sectionName="Stats" data-id="element-1446">
        <Stats language={language} data-id="element-1447" />
      </SectionTracker>

      <SectionTracker sectionName="About" data-id="element-1448">
        <About language={language} data-id="element-1449" />
      </SectionTracker>

      <SectionTracker sectionName="Portfolio Performance" data-id="element-1450">
        <Suspense fallback={<LoadingFallback data-id="element-1452" />} data-id="element-1451">
          <PortfolioPerformance language={language} data-id="element-1453" />
        </Suspense>
      </SectionTracker>

      <SectionTracker sectionName="Monte Carlo Simulation" data-id="element-1454">
        <Suspense fallback={<LoadingFallback data-id="element-1456" />} data-id="element-1455">
          <MonteCarloSimulation language={language} data-id="element-1457" />
        </Suspense>
      </SectionTracker>

      <SectionTracker sectionName="Travel Map" data-id="element-1458">
        <Suspense fallback={<LoadingFallback data-id="element-1460" />} data-id="element-1459">
          <TravelMap language={language} data-id="element-1461" />
        </Suspense>
      </SectionTracker>

      <SectionTracker sectionName="Photo Gallery" data-id="element-1462">
        <PhotoGallery language={language} data-id="element-1463" />
      </SectionTracker>

      <SectionTracker sectionName="Education" data-id="element-1464">
        <Education language={language} data-id="element-1465" />
      </SectionTracker>

      <SectionTracker sectionName="Experience" data-id="element-1466">
        <Experience language={language} data-id="element-1467" />
      </SectionTracker>

      <SectionTracker sectionName="Certifications" data-id="element-1468">
        <Certifications language={language} data-id="element-1469" />
      </SectionTracker>

      <SectionTracker sectionName="Skills" data-id="element-1470">
        <Skills language={language} data-id="element-1471" />
      </SectionTracker>

      <SectionTracker sectionName="Organizations Founded" data-id="element-1472">
        <OrganizationsFounded language={language} data-id="element-1473" />
      </SectionTracker>

      <SectionTracker sectionName="Featured Press" data-id="element-1474">
        <FeaturedPress language={language} data-id="element-1475" />
      </SectionTracker>

      <SectionTracker sectionName="Projects" data-id="element-1476">
        <Projects language={language} data-id="element-1477" />
      </SectionTracker>

      <SectionTracker sectionName="Leadership" data-id="element-1478">
        <Leadership language={language} data-id="element-1479" />
      </SectionTracker>

      <SectionTracker sectionName="Contact" data-id="element-1480">
        <Contact language={language} data-id="element-1481" />
      </SectionTracker>

      <Footer language={language} data-id="element-1482" />
      <MobileContactFAB language={language} data-id="element-1483" />
      <AIAssistant data-id="element-1484" />
      <ExitIntentModal data-id="element-1485" />
      <AnalyticsPanel data-id="element-1486" />
    </div>;
};
export default Home;